import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { reviews, faqItems } from '@/mocks/reviews';

const homepageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '17c.com',
  url: 'https://17c.com/',
  description: '17c.com 是您值得信赖的在线平台，提供丰富的内容与服务资源。',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://17c.com/search?q={search_term_string}',
    'query-input': 'required name=search_term_string',
  },
};

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '17c.com',
  url: 'https://17c.com/',
  description: '提供丰富在线内容与服务的综合平台',
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'support@17c.com',
    contactType: 'customer service',
    availableLanguage: 'Chinese',
  },
};

export default function Home() {
  const [openFaq, setOpenFaq] = useState<string | null>(null);

  const featuredArticles = articles.slice(0, 6);
  const latestArticles = articles.slice(0, 4);

  return (
    <>
      <SEOHead
        title="17c.com – 官方入口与核心服务导览"
        description="17c.com 是您值得信赖的在线平台，提供丰富的内容与服务资源。本页详细介绍 17c.com 的核心功能、使用指南、用户评价及常见问题解答，帮助您快速了解并充分利用 17c.com 的全部价值。"
        keywords="17c.com,17c.com官网,17c.com入口,17c.com使用指南,17c.com功能介绍,17c.com用户评价,17c.com注册登录"
        canonicalUrl="https://17c.com/"
        schema={{ ...homepageSchema, ...organizationSchema }}
      />

      {/* Hero Section */}
      <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20digital%20platform%20workspace%20with%20warm%20ambient%20lighting%20featuring%20abstract%20geometric%20patterns%20in%20soft%20golden%20and%20cream%20tones%20clean%20spacious%20composition%20professional%20atmosphere%20editorial%20photography%20with%20gentle%20gradients%20and%20refined%20aesthetic&width=1600&height=900&seq=hero-17c-01&orientation=landscape"
          alt="17c.com 官方平台"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/15 to-black/35"></div>
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4 w-full">
          <div className="max-w-3xl mx-auto">
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6 leading-tight">
              17c.com – 您值得信赖的在线平台
            </h1>
            <p className="text-base md:text-lg text-white/85 max-w-2xl mx-auto mb-6 md:mb-8 leading-relaxed">
              提供丰富的内容与服务资源，智能推荐、社区互动、数据分析，全方位满足您的需求
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/guide"
                className="px-7 py-3 rounded-lg bg-primary-500 text-background-50 font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
              >
                立即探索
              </Link>
              <a
                href="/about"
                className="px-7 py-3 rounded-lg bg-white/20 text-white backdrop-blur-sm font-medium hover:bg-white/30 transition-colors duration-200 whitespace-nowrap"
              >
                了解更多
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="w-full px-4 md:px-6 lg:px-8 py-12 md:py-16 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              核心功能分类
            </h2>
            <p className="text-foreground-500 max-w-xl mx-auto">
              按类别浏览内容，快速找到您需要的信息
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className="group p-5 rounded-lg bg-background-100 hover:bg-background-200/70 transition-colors duration-200"
              >
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className={`${cat.icon} text-xl text-accent-700`}></i>
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground-900 mb-1 group-hover:text-primary-600 transition-colors">
                      {cat.name}
                    </h3>
                    <p className="text-sm text-foreground-500 leading-relaxed mb-2">
                      {cat.description}
                    </p>
                    <span className="text-xs text-foreground-400">
                      {cat.articleCount} 篇文章
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Articles */}
      <section className="w-full px-4 md:px-6 lg:px-8 py-12 md:py-16 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                精选文章
              </h2>
              <p className="text-foreground-500 text-sm">编辑团队为您推荐的高质量内容</p>
            </div>
            <Link
              to="/category/guide"
              className="hidden sm:flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors whitespace-nowrap"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {featuredArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.slug}`}
                className="group rounded-lg bg-background-50 hover:bg-background-200/50 transition-colors duration-200 overflow-hidden"
              >
                <div className="h-44 overflow-hidden">
                  <img
                    src={article.imageUrl}
                    alt={article.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <span className="inline-block px-2.5 py-0.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-2">
                    {article.categoryName}
                  </span>
                  <h3 className="font-heading font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors truncate-2">
                    {article.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed mb-3 truncate-2">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-foreground-400">
                    <span>{article.publishDate}</span>
                    <span>{article.readTime} 分钟阅读</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-6 text-center sm:hidden">
            <Link
              to="/category/guide"
              className="inline-flex items-center gap-1 text-sm text-primary-600 font-medium"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Highlights */}
      <section className="w-full px-4 md:px-6 lg:px-8 py-12 md:py-16 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              为什么选择 17c.com
            </h2>
            <p className="text-foreground-500 max-w-xl mx-auto">我们的核心优势</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: 'ri-shield-star-line', title: '安全可靠', desc: '多层加密保护，定期安全审计，守护您的数据和隐私安全' },
              { icon: 'ri-flashlight-line', title: '高效智能', desc: 'AI 智能推荐引擎，精准匹配您感兴趣的内容，节省浏览时间' },
              { icon: 'ri-group-line', title: '活跃社区', desc: '数百万用户共建的活跃社区，分享见解，互动交流' },
              { icon: 'ri-device-line', title: '多端同步', desc: '电脑、平板、手机无缝切换，随时随地访问您的专属内容' },
            ].map((feature, i) => (
              <div key={i} className="p-5 rounded-lg bg-background-100 text-center">
                <div className="w-12 h-12 rounded-full bg-secondary-100 flex items-center justify-center mx-auto mb-4">
                  <i className={`${feature.icon} text-xl text-secondary-700`}></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Articles */}
      <section className="w-full px-4 md:px-6 lg:px-8 py-12 md:py-16 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                最新内容
              </h2>
              <p className="text-foreground-500 text-sm">持续更新的优质内容</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {latestArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.slug}`}
                className="group p-5 rounded-lg bg-background-50 hover:bg-background-200/50 transition-colors duration-200"
              >
                <span className="inline-block px-2.5 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-3">
                  {article.categoryName}
                </span>
                <h3 className="font-heading font-semibold text-foreground-900 text-sm leading-snug mb-2 group-hover:text-primary-600 transition-colors truncate-2">
                  {article.title}
                </h3>
                <p className="text-xs text-foreground-500 leading-relaxed mb-3 truncate-2">
                  {article.excerpt}
                </p>
                <div className="flex items-center gap-3 text-xs text-foreground-400">
                  <span>{article.publishDate}</span>
                  <span>{article.readTime} 分钟阅读</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="w-full px-4 md:px-6 lg:px-8 py-12 md:py-16 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              用户评价
            </h2>
            <p className="text-foreground-500 max-w-xl mx-auto">听听用户们怎么说</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {reviews.slice(0, 3).map((review) => (
              <div key={review.id} className="p-5 rounded-lg bg-background-100">
                <div className="flex items-center gap-3 mb-4">
                  <img
                    src={review.avatar}
                    alt={review.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-medium text-foreground-900 text-sm">{review.name}</p>
                    <p className="text-xs text-foreground-400">{review.role}</p>
                  </div>
                </div>
                <div className="flex items-center gap-0.5 mb-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i
                      key={i}
                      className={`ri-star-fill text-sm ${i < review.rating ? 'text-primary-500' : 'text-foreground-200'}`}
                    ></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed">{review.content}</p>
                <p className="text-xs text-foreground-400 mt-3">{review.date}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link
              to="/about#reviews"
              className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
            >
              查看更多评价 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="w-full px-4 md:px-6 lg:px-8 py-12 md:py-16 bg-background-100">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              常见问题解答
            </h2>
            <p className="text-foreground-500">快速找到您需要的答案</p>
          </div>
          <div className="flex flex-col gap-3">
            {faqItems.map((faq) => (
              <div
                key={faq.id}
                className="rounded-lg bg-background-50 overflow-hidden"
              >
                <button
                  className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-background-100/70 transition-colors"
                  onClick={() => setOpenFaq(openFaq === faq.id ? null : faq.id)}
                >
                  <span className="font-medium text-foreground-900 text-sm pr-4">{faq.question}</span>
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className={`${openFaq === faq.id ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-400`}></i>
                  </span>
                </button>
                {openFaq === faq.id && (
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link
              to="/category/faq-articles"
              className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
            >
              查看全部 FAQ <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative w-full px-4 md:px-6 lg:px-8 py-16 md:py-20 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Warm%20abstract%20geometric%20background%20with%20soft%20golden%20and%20cream%20gradients%20flowing%20organic%20patterns%20elegant%20minimalist%20design%20professional%20atmosphere%20gentle%20warm%20lighting%20sophisticated%20aesthetic&width=1600&height=600&seq=cta-17c-02&orientation=landscape"
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/40"></div>
        <div className="relative z-10 max-w-2xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
            准备好开始了吗？
          </h2>
          <p className="text-white/80 mb-7">
            立即加入 17c.com，探索丰富内容，连接精彩世界
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/category/guide"
              className="px-7 py-3 rounded-lg bg-primary-500 text-background-50 font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
            >
              免费注册
            </Link>
            <Link
              to="/about"
              className="px-7 py-3 rounded-lg bg-white/20 text-white backdrop-blur-sm font-medium hover:bg-white/30 transition-colors duration-200 whitespace-nowrap"
            >
              关于我们
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}